import { useState, useEffect, useCallback } from "react";
import { Session, InterviewSessionFlags } from "../../../../Entities/Session";
import { Question } from "../../../../Entities/Question";
import { InterviewRound } from "../../../../Dto/GenerateJobDescription.dto";
import interviewService from "../../../../Service/interview.service";

export interface RoundStatistics {
  sessionId: string;
  interviewRoundId: string;
  roundName: string;
  roundNumber: number;
  attemptNumber: number;
  isRestarted: boolean;
  status: "pending" | "qualified" | "unqualified";
  totalQuestions: number;
  attemptedQuestions: number;
  qualifiedQuestions: number;
  unqualifiedQuestions: number;
  pendingQuestions: number;
  hasViolations: boolean;
  violationCount: number;
  questions: Question[];
  updatedAt: Date;
}

export interface AssessmentStatistics {
  rounds: RoundStatistics[];
  totalRounds: number;
  qualifiedRounds: number;
  unqualifiedRounds: number;
  pendingRounds: number;
  totalQuestions: number;
  totalAttempted: number;
  totalQualified: number;
  totalUnqualified: number;
  totalPending: number;
  roundsWithViolations: number;
}

interface UseAssessmentStatisticsProps {
  interviewSessions: Session[];
  interviewRounds: InterviewRound[];
  jobPostInterviewRounds: { interviewRoundId: string; interviewRoundNumber: number }[];
}

const checkForViolations = (flags: InterviewSessionFlags): { hasViolations: boolean; count: number } => {
  if (!flags) return { hasViolations: false, count: 0 };
  
  const violations = [
    flags.fullscreenExited,
    flags.multipleTabsDetected,
    flags.networkInterrupted,
    flags.keyboardViolation,
    flags.screenRecordingStopped,
    flags.fullscreenExitAttempt,
  ];
  
  const count = violations.filter(Boolean).length;
  return { hasViolations: count > 0, count };
};

export const useAssessmentStatistics = ({
  interviewSessions,
  interviewRounds,
  jobPostInterviewRounds,
}: UseAssessmentStatisticsProps) => {
  const [statistics, setStatistics] = useState<AssessmentStatistics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStatistics = useCallback(async () => {
    if (!interviewSessions.length || !interviewRounds.length) {
      setStatistics(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Group sessions by interviewRoundId to track attempts (for restart detection)
      const sessionsByRound: Record<string, Session[]> = {};
      interviewSessions.forEach((session) => {
        const roundId = session.interviewRoundId;
        if (!sessionsByRound[roundId]) {
          sessionsByRound[roundId] = [];
        }
        sessionsByRound[roundId].push(session);
      });

      // Sort each round's sessions by createdAt to determine attempt order
      // Secondary sort by _id for deterministic order when timestamps match
      Object.values(sessionsByRound).forEach((sessions) => {
        sessions.sort((a, b) => {
          const timeDiff = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          if (timeDiff !== 0) return timeDiff;
          return (a._id || '').localeCompare(b._id || '');
        });
      });

      const roundsData: RoundStatistics[] = await Promise.all(
        interviewSessions.map(async (session) => {
          const roundDetails = interviewRounds.find(
            (round) => round._id === session.interviewRoundId
          );
          const jobPostRound = jobPostInterviewRounds.find(
            (round) => round.interviewRoundId === session.interviewRoundId
          );

          // Calculate attempt number for restart tracking
          const roundSessions = sessionsByRound[session.interviewRoundId] || [];
          const attemptNumber = roundSessions.findIndex((s) => s._id === session._id) + 1;
          const isRestarted = roundSessions.length > 1;

          let questions: Question[] = [];
          try {
            questions = (await interviewService.getQuestionsBySessionId(session._id)) || [];
          } catch (err) {
            console.error(`Failed to fetch questions for session ${session._id}:`, err);
          }

          const attemptedQuestions = questions.filter(
            (q) => q.responses && q.responses.length > 0
          ).length;

          const qualifiedQuestions = questions.filter(
            (q) => q.evaluation_status === "qualified"
          ).length;

          const unqualifiedQuestions = questions.filter(
            (q) => q.evaluation_status === "unqualified"
          ).length;

          const pendingQuestions = questions.filter(
            (q) => !q.evaluation_status
          ).length;

          const { hasViolations, count: violationCount } = checkForViolations(session.flags);

          return {
            sessionId: session._id,
            interviewRoundId: session.interviewRoundId,
            roundName: roundDetails?.title || "Unknown Round",
            roundNumber: jobPostRound?.interviewRoundNumber || 0,
            attemptNumber,
            isRestarted,
            status: session.status,
            totalQuestions: questions.length,
            attemptedQuestions,
            qualifiedQuestions,
            unqualifiedQuestions,
            pendingQuestions,
            hasViolations,
            violationCount,
            questions,
            updatedAt: session.updatedAt,
          };
        })
      );

      // Sort by round number
      const sortedRounds = roundsData.sort((a, b) => a.roundNumber - b.roundNumber);

      const aggregatedStats: AssessmentStatistics = {
        rounds: sortedRounds,
        totalRounds: sortedRounds.length,
        qualifiedRounds: sortedRounds.filter((r) => r.status === "qualified").length,
        unqualifiedRounds: sortedRounds.filter((r) => r.status === "unqualified").length,
        pendingRounds: sortedRounds.filter((r) => r.status === "pending").length,
        totalQuestions: sortedRounds.reduce((sum, r) => sum + r.totalQuestions, 0),
        totalAttempted: sortedRounds.reduce((sum, r) => sum + r.attemptedQuestions, 0),
        totalQualified: sortedRounds.reduce((sum, r) => sum + r.qualifiedQuestions, 0),
        totalUnqualified: sortedRounds.reduce((sum, r) => sum + r.unqualifiedQuestions, 0),
        totalPending: sortedRounds.reduce((sum, r) => sum + r.pendingQuestions, 0),
        roundsWithViolations: sortedRounds.filter((r) => r.hasViolations).length,
      };

      setStatistics(aggregatedStats);
    } catch (err) {
      console.error("Error fetching assessment statistics:", err);
      setError("Failed to fetch assessment statistics");
    } finally {
      setLoading(false);
    }
  }, [interviewSessions, interviewRounds, jobPostInterviewRounds]);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  return { statistics, loading, error, refetch: fetchStatistics };
};

export default useAssessmentStatistics;



